import * as React from "react";

import {Dispatcher, ValidationError} from "../extensions/Dispatcher";
import {ApiClient} from "../../../services/ApiClient";
import {Component, Handler, I18NString, isNullOrEmpty} from "../../Utils";
import {IControlSchema} from "../../Controls";
import {Description} from "../extensions/Description";
import {IDispatcher } from '../extensions/Dispatcher'

import {createTextBox} from "./TextBox";
import {createDropBox, IDropBoxSchema} from "./DropBox";
import {Captions} from "../../../messages";

const _controls : {[type: string]: Handler} = {
    "TEXTBOX": createTextBox,
    "TEXTAREA": createTextBox,
    "DROPBOX": createDropBox
};

export type ChangeHandler = (id: string, value: any, valid: boolean, error: ValidationError | undefined) => void;

export class ObjectControlDispatcher extends Dispatcher {
    private change: ChangeHandler;

    constructor(change: Handler) {
        super();
        this.change = change;
    }

    onVisit(id: string) {
    }

    setError(id: string, value: string, error: { message: any; }, skipCommonValidation: boolean): void {
    }

    onChange(id: string, value: any, valid: boolean, error: ValidationError | undefined): void {
        this.change(id, value, valid, error);
    }

    onBlur(id: string, value: any, valid: boolean): void {
        
    }

    onLoadFile(id: string, files: Array<any>): void {
        
    }

    onAddValueRefresh(id: string, value: any): void {
    }
    

    public lock(value: boolean) {
        this.invoke("lock", value);
    }

    public clear(values: {[id: string]: any} = null) {
        this.invoke("clear");
    }
}

export interface IObjectControlSchema extends IControlSchema {
    description?: I18NString;
    controls: IControlSchema[];
    views: string[];
    value: any[];
}

export interface IObjectControlProps {
    schema: IObjectControlSchema;
    api: ApiClient;
}

export interface IObjectControlState {
    values: any[];
    currentValues: {[id: string]: {[key: string]: any}};
    active: boolean;
}

export class ObjectControl extends Component<IObjectControlProps, IObjectControlState> {

    private viewControls: IControlSchema[];

    private dispatcher: ObjectControlDispatcher;

    constructor() {
        super();

        this.state = {
            values: [],
            currentValues: {},
            active: false
        };

        this.renderValues = this.renderValues.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSend = this.onSend.bind(this);

        this.dispatcher = new ObjectControlDispatcher(this.onChange);
    }

    private getDisplayValue(value: any, control: IControlSchema): any {
        if(control.type.toUpperCase() == "DROPBOX") {
            const dropBox = control as IDropBoxSchema;
            for(const opt of dropBox.options) {
                if(opt.key == value) {
                    return this.tr(opt.value);
                }
            }
        }
        return value;
    }

    componentDidMount() {
        this.viewControls = [];
        for(const control of this.props.schema.controls) {
            if(this.props.schema.views.indexOf(control.id) > -1)
                this.viewControls.push(control);
        }

        if(this.props.schema.value != null)
            this.setState({values: this.props.schema.value});
    }

    protected onChange(id: string, value: any, valid: boolean) {
        const values = this.state.currentValues;
        values[id] = { value: value, valid: valid };

        if(valid) {
            for(const control of this.props.schema.controls) {
                const value = values[control.id];
                if(value == undefined && control.required || !value.valid)
                    valid = false
            }
        }

        this.setState({currentValues: values, active: valid});
    }

    protected onSend(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        if(this.state.active == false)
            return;

        this.dispatcher.lock(true);
        this.setState({active: false});

        const values : {[id: string]: any} = {};
        for(const control of this.props.schema.controls) {
            const value = this.state.currentValues[control.id];
            if(value != undefined)
                values[control.id] = value.value;
        }

        const self = this;
        const timestamp = this.props.api.putValue(
            this.props.schema.id,
            values,
            {
                success: params => {
                    const stateValues = this.state.values;
                    stateValues.push({timestamp: timestamp, value: values});

                    self.setState({active: false, values: stateValues, currentValues: {}});
                    self.dispatcher.lock(false);
                    self.dispatcher.clear();
                },
                error: (status2, error) => {
                    self.dispatcher.lock(false);

                    self.setState({active: true});
                }
            }
        )
    }

    private removeHandler(timestamp: number): any {
        const self = this;
        const fn = function (e: React.MouseEvent<any>) {
            e.preventDefault();
            this.props.api.removeValue(
                this.props.schema.id,
                timestamp, {
                    success: () => {
                        let values = self.state.values;
                        for(const value of values) {
                            if(value.timestamp == timestamp) {
                                const ix = values.indexOf(value);
                                if(ix > -1) {
                                    values.splice(ix, 1);
                                }
                            }
                        }
                        self.setState({values: values});
                    },
                    error: () => {

                    }
                }
            )
        };
        return fn.bind(this);
    }

    private renderValues(): React.ReactElement<any> {
        if(this.state.values.length > 0) {
            const headers = [];
            for(const control of this.viewControls) {
                headers.push(<th>{this.tr(control.title)}</th>)
            }
            if(!this.props.api.isLocked())
                headers.push(<th className="row-controls"/>);

            const values : React.ReactElement<any>[] = [];
            for(const value of this.state.values) {
                const row = [];
                for(const control of this.viewControls) {
                    let displayValue = this.getDisplayValue(value.value[control.id], control);
                    row.push(<td>{displayValue}</td>);
                }
                if(!this.props.api.isLocked()) {
                    row.push(
                        <td>
                            <button onClick={this.removeHandler(value.timestamp)} className="btn btn-danger btn-mini">
                                <i className="icon-remove"/>
                            </button>
                        </td>
                    );
                }
                values.push(<tr>{row}</tr>)
            }

            return (
                <table className="table table-striped table-bordered">
                    <thead>
                        <tr>{headers}</tr>
                    </thead>
                    <tbody>
                        {values}
                    </tbody>
                </table>
            );
        }
        return null;
    }

    render() {
        let description = null;
        if(this.props.schema.description != null) {
            description = [
                // tslint:disable-next-line:jsx-key
                <span>&nbsp;</span>,
                // tslint:disable-next-line:jsx-key
                <Description text={this.props.schema.description}/>
            ];
        }

        const controls : React.ReactElement<any>[] = [];
        for (const control of this.props.schema.controls) {
            const handler = _controls[control.type.toUpperCase()];
            if(handler != null) {
                controls.push(
                    handler(control, this.props.api, this.dispatcher)
                );
            }
        }

        let btnClassName = "btn btn-primary";
        if(!this.state.active)
            btnClassName += " disabled";

        return (
            <div>
                <label>{this.tr(this.props.schema.title)}{description}</label>
                {this.renderValues()}
                <div className="well">
                    {controls}
                    <div className="control-group text-right">
                        <button className={btnClassName} onClick={this.onSend}>{this.tr(Captions["ADD"])}</button>
                    </div>
                </div>
            </div>
        );
    }
}

export function createObjectControl(schema: IControlSchema, api: ApiClient, dispatcher:IDispatcher=null): React.ReactElement<any> {
    return <ObjectControl schema={schema as IObjectControlSchema} api={api}/>;
}


// WEBPACK FOOTER //
// ./src/components/controls/standart/ObjectControl.tsx